import { Fragment } from 'react';
import type { ComponentType } from 'react';
import { GRUPO_DESTAQUE_META, type GrupoModulo } from '../data';
import MockDashboard from './mockups/MockDashboard';
import MockGamificacao from './mockups/MockGamificacao';
import MockCarteira from './mockups/MockCarteira';
import MockSeguranca from './mockups/MockSeguranca';

const MOCKUPS: Record<string, ComponentType> = {
  estrategia: MockDashboard,
  engajamento: MockGamificacao,
  premiacao: MockCarteira,
  confianca: MockSeguranca,
};

interface PlataformaGrupoDestaqueProps {
  grupo: GrupoModulo;
  reverse?: boolean;
}

export default function PlataformaGrupoDestaque({ grupo, reverse = false }: PlataformaGrupoDestaqueProps) {
  const meta = GRUPO_DESTAQUE_META[grupo.id];
  const Mockup = MOCKUPS[grupo.id];

  if (!meta || !Mockup) return null;

  return (
    <section id={grupo.id} className="relative scroll-mt-28 overflow-hidden pb-16 pt-12 md:pb-24 md:pt-16">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className={`absolute -top-28 h-[620px] w-[620px] rounded-full bg-primary-200/50 blur-3xl ${
            reverse ? '-left-44' : '-right-44'
          }`}
        ></div>
        <div
          className={`absolute top-[26%] h-[420px] w-[420px] rounded-full bg-primary-100/70 blur-3xl ${
            reverse ? 'left-[16%]' : 'right-[16%]'
          }`}
        ></div>
        <div
          className={`absolute bottom-[-60px] h-[340px] w-[340px] rounded-full bg-accent-100/50 blur-3xl ${
            reverse ? '-right-28' : '-left-28'
          }`}
        ></div>
      </div>

      <div className="relative mx-auto w-full max-w-[1240px] px-6">
        <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-[0.92fr_1.08fr] lg:gap-10">
          {/* Coluna de texto */}
          <div className={`lg:pt-6 ${reverse ? 'lg:order-2' : ''}`}>
            <span className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-primary-700">
              <i className={`${meta.eyebrowIcon} text-sm`}></i>
              {meta.eyebrow}
            </span>

            <h3 className="mt-5 font-heading text-3xl font-extrabold leading-[1.06] tracking-[-0.02em] text-foreground-950 md:text-[2.75rem]">
              {meta.titleTop}
              <br />
              {meta.titleBottomPrefix}{' '}
              {meta.titleHighlight && <span className="text-primary-500">{meta.titleHighlight}</span>}
            </h3>

            <p className="mt-5 max-w-md text-sm leading-relaxed text-foreground-600 md:text-base">
              {grupo.description}
            </p>
          </div>

          {/* Mockup */}
          <div className={`relative lg:pt-10 ${reverse ? 'lg:order-1' : ''}`}>
            {meta.annotationTop && (
              <span
                className={`pointer-events-none absolute top-0 hidden font-hand text-xl font-bold text-primary-600 md:block md:text-2xl ${
                  reverse ? 'right-0 md:rotate-6' : 'left-0 md:-rotate-6'
                }`}
              >
                {meta.annotationTop}
              </span>
            )}
            <Mockup />
          </div>
        </div>

        {/* Cards numerados dos módulos */}
        <div className="relative mt-12 md:mt-14">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:flex lg:items-stretch lg:gap-1">
            {grupo.modulos.map((modulo, index) => (
              <Fragment key={modulo.title}>
                <article
                  className={`flex flex-1 flex-col rounded-2xl border bg-background-50 p-5 transition-colors ${
                    index === 1
                      ? 'border-primary-300'
                      : 'border-background-200 hover:border-primary-300'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-500 text-white">
                      <i className={`${modulo.icon} text-lg`}></i>
                    </span>
                    <span className="font-heading text-sm font-bold text-foreground-300">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <h4 className="mt-4 font-heading text-base font-bold leading-snug text-foreground-950">
                    {modulo.title}
                  </h4>
                  <p className="mt-2.5 text-sm leading-relaxed text-foreground-600">{modulo.description}</p>
                </article>
                {index < grupo.modulos.length - 1 && (
                  <div className="hidden shrink-0 items-center justify-center lg:flex">
                    <i className="ri-arrow-right-s-line text-2xl text-primary-500"></i>
                  </div>
                )}
              </Fragment>
            ))}
          </div>

          {(meta.annotationBottom || meta.floating) && (
            <div className="mt-8 flex flex-col items-end gap-4 lg:flex-row lg:items-center lg:justify-end lg:gap-8">
              {meta.annotationBottom && (
                <span className="font-hand text-xl font-bold text-primary-600 md:-rotate-3 md:text-2xl">
                  {meta.annotationBottom}
                </span>
              )}
              {meta.floating && (
                <div className="flex items-center gap-3 rounded-2xl bg-primary-500 px-5 py-3.5 text-white">
                  <i className={`${meta.floatingIcon} text-2xl`}></i>
                  <span className="max-w-[160px] text-sm font-semibold leading-tight">{meta.floating}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}